import { ref, computed } from 'vue';
import { FEATURES_CONFIG, type FeatureKey } from '@/config/features';

const STORAGE_KEY = 'ga-feature-flags-overrides';
const overrides = ref<Record<string, boolean>>(loadOverrides());

function loadOverrides(): Record<string, boolean> {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored ? JSON.parse(stored) : {};
  } catch {
    return {};
  }
}

function saveOverrides() {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(overrides.value));
}

export function useFeatureFlags() {
  const isEnabled = (key: FeatureKey): boolean => {
    if (overrides.value[key] !== undefined) {
      return overrides.value[key];
    }
    // Mode normalization: `npm run dev:local` sets MODE=localdev, which is
    // documented in .env.localdev as "estende development". Treat it as
    // development for flag resolution so local-backend workflows see the
    // same flags as `npm run dev`. Any other unknown mode still falls
    // through to the production default below.
    const rawMode = import.meta.env.MODE;
    const mode = (rawMode === 'localdev' ? 'development' : rawMode) as
      | 'development'
      | 'staging'
      | 'production';
    const config = FEATURES_CONFIG[key];
    if (!config) return false;

    return config[mode] ?? config['production'] ?? false;
  };

  const setOverride = (key: FeatureKey, value: boolean | undefined) => {
    if (value === undefined) {
      delete overrides.value[key];
    } else {
      overrides.value[key] = value;
    }
    saveOverrides();
  };

  const clearOverrides = () => {
    overrides.value = {};
    saveOverrides();
  };

  return {
    isEnabled,
    setOverride,
    clearOverrides,
    overrides: computed(() => overrides.value),
  };
}
